<script lang="ts">
    import { globalLoggerToken } from "@/tokens";
    import DOMClipboardAdapter from "@lib/DOMClipboardAdapter/DOMClipboardAdapter";
    import { container } from "tsyringe";

    import BaseButton from "./Buttons/BaseButton.svelte";
    import ButtonWithIcon from "./Buttons/ButtonWithIcon.svelte";
    import Icon from "./Icon.svelte";
    import CopyToClipboardIcon from "./icons/CopyToClipboardIcon.svelte";

    export let text: string;
    export let buttonInsideInput = false;

    let inputRef: HTMLInputElement;
    let copied = false;

    const globalLogger = container.resolve(globalLoggerToken);
    const clipboard = new DOMClipboardAdapter();

    const fallbackCopyTextToClipboard = () => {
        // Avoid scrolling to bottom
        inputRef.style.top = "0";
        inputRef.style.left = "0";
        inputRef.style.position = "fixed";

        inputRef.focus();
        inputRef.select();

        try {
            var successful = document.execCommand("copy");
            if (successful) {
                copied = true;
                setTimeout(() => {
                    copied = false;
                }, 2000);
            }
        } catch (err) {
            globalLogger.error("Fallback: Oops, unable to copy", err);
        }
    };

    const copyTextToClipboard = () => {
        if (!clipboard) {
            fallbackCopyTextToClipboard();
            return;
        }
        clipboard
            .writeText(text)
            .then(() => {
                copied = true;

                setTimeout(() => {
                    copied = false;
                }, 2000);
            })
            .catch((err) => {
                globalLogger.error("Async: Could not copy text: ", err);
            });
    };
</script>

<div class="gw-copy-to-clipboard">
    <div class="gw-copy-to-clipboard__input-container">
        <input
            class="gw-copy-to-clipboard__input"
            value={text}
            readonly
            bind:this={inputRef}
        />
        {#if buttonInsideInput}
            <div class="gw-copy-to-clipboard__btn-wrapper">
                <ButtonWithIcon
                    big
                    class="gw-copy-to-clipboard__inside-input-btn"
                    on:click={copyTextToClipboard}
                >
                    <Icon width="24" height="24">
                        <CopyToClipboardIcon />
                    </Icon>
                </ButtonWithIcon>
                {#if copied}
                    <span class="gw-copy-to-clipboard__copied-msg">Copied</span>
                {/if}
            </div>
        {/if}
    </div>
    {#if !buttonInsideInput}
        <div class="gw-copy-to-clipboard__btn-wrapper">
            <BaseButton
                type="button"
                on:click={copyTextToClipboard}
                class="gw-copy-to-clipboard__btn"
            >
                <Icon width="24" height="24">
                    <CopyToClipboardIcon />
                </Icon>
            </BaseButton>
            {#if copied}
                <span class="gw-copy-to-clipboard__copied-msg">Copied</span>
            {/if}
        </div>
    {/if}
</div>

<style lang="postcss">
    .gw-copy-to-clipboard {
        width: 100%;
        display: flex;
        gap: 8px;

        &__input-container {
            flex: 1;
            display: flex;
            align-items: center;
            border: 1px dashed rgba(29, 41, 57, 0.6);
            border-radius: 8px;
            padding: 0 8px;
        }

        &__input {
            border: none;
            width: 100%;
            background-color: transparent;
            outline: none;
            text-align: center;
            padding: 8px 0;
            margin: 0;
            font-size: 16px;
            line-height: 1.2;
        }

        &__btn-wrapper {
            position: relative;
        }

        &__copied-msg {
            position: absolute;
            top: -24px;
            font-size: 12px;
            left: 50%;
            transform: translateX(-50%);
        }
    }

    :global(.gw-btn.gw-copy-to-clipboard__btn) {
        padding: 8px 10px;
        border: 1px solid rgba(29, 41, 57, 0.6);
        border-radius: 10px;
    }

    :global(.gw-btn.gw-copy-to-clipboard__inside-input-btn) {
        margin-right: 10px;
    }
</style>
